import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import type { OrgItem } from '@/lib/apiTypes'
import { useOrgMembers } from '@/lib/hooks'
import { InviteOrgMemberModal } from '@/features/org/components/InviteOrgMemberModal'
import { OrgPolicies } from '@/features/policies/OrgPoliciesPanel'
import { OrgBilling } from '@/features/org/tabs/BillingTab'
import { api } from '@/lib/api'
import { formatDate, formatDateTimeSeconds, relativeTime } from '@/lib/format'
import { RoleBadge } from '@/features/org/components/RoleBadge'
import { ConsumptionBlockPanel } from '@/features/org/components/ConsumptionBlockPanel'
import { EmptyState } from '@/shared/components/EmptyState'
import { SkeletonTable } from '@/shared/components/Skeleton'
import { FeedbackMessage } from '@/shared/components/FeedbackMessage'
import { useToast } from '@/context/ToastContext'
import s from '@/features/org/org.module.css'
import { Tooltip } from '@/shared/components/Tooltip'
import { DeleteOrgSection } from '@/features/org/components/DeleteOrgSection'
import { LeaveOrgSection } from '@/features/org/components/LeaveOrgSection'
import { NotificationsSettings } from '@/features/org/tabs/NotificationsTab'
import { VcsTrustCard } from '@/features/org/tabs/VcsTrustTab'
import { AccessRulesTab } from '@/features/org/tabs/AccessRulesTab'
import { type OrgTab } from '@/features/org/tabs/orgTabs'
import { Tabs } from '@/shared/components/Tabs'
import { ORG_TABS } from '@/features/org/tabs/orgTabs'

export function OrgDetail({ org, onUpdated, initialTab }: { org: OrgItem; onUpdated: () => void; initialTab?: OrgTab }) {
  const members = useOrgMembers(org.slug)
  const { showToast } = useToast()
  const ROLE_RANK: Record<string, number> = { reader: 1, member: 2, admin: 3, owner: 4 }
  const rank = ROLE_RANK[org.role] ?? 0
  const [activeTab, setActiveTab]           = useState<OrgTab>(initialTab ?? 'members')
  const [showInvite, setShowInvite]         = useState(false)
  const [claimDomain, setClaimDomain]       = useState('')
  const [busy, setBusy]                     = useState<string | null>(null)
  const [confirmRemove, setConfirmRemove]   = useState<string | null>(null)

  const canAdmin = ['owner', 'admin'].includes(org.role)
  const isOwner  = org.role === 'owner'

  async function handleRoleChange(userId: string, role: string) {
    setBusy(userId)
    try {
      await api.patch(`/api/orgs/${org.slug}/members/${userId}`, { role })
      members.refetch()
      showToast('Role updated.', 'success')
    } catch (err) {
      showToast((err as Error).message, 'error')
    } finally {
      setBusy(null)
    }
  }

  async function handleRemove(userId: string) {
    setConfirmRemove(null)
    setBusy(userId + '-del')
    try {
      await api.delete(`/api/orgs/${org.slug}/members/${userId}`)
      members.refetch()
      showToast('Member removed.', 'success')
    } catch (err) {
      showToast((err as Error).message, 'error')
    } finally {
      setBusy(null)
    }
  }

  async function handleClaimDomain(e: React.FormEvent) {
    e.preventDefault()
    setBusy('claim')
    try {
      await api.post(`/api/orgs/${org.slug}/claim-domain`, { domain: claimDomain })
      setClaimDomain('')
      showToast(`Domain "${claimDomain}" claimed. New signups with this domain will auto-join as reader.`, 'success')
      onUpdated()
    } catch (err) {
      showToast((err as Error).message, 'error')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div>
      {showInvite && (
        <InviteOrgMemberModal
          orgId={org.slug}
          orgName={org.name}
          myRole={org.role}
          onClose={() => setShowInvite(false)}
          onInvited={() => { setShowInvite(false); members.refetch(); showToast('Invitation sent.', 'success') }}
        />
      )}

      <Tabs
        tabs={ORG_TABS}
        value={activeTab}
        onChange={setActiveTab}
        label="Settings sections"
        right={canAdmin && (
          <button className="btn btn-primary btn-sm" onClick={() => setShowInvite(true)}>
            + Invite member
          </button>
        )}
      />

      {activeTab === 'vcs-trust' && (
        rank >= 3
          ? <VcsTrustCard orgId={org.slug} canAdmin={canAdmin} />
          : <EmptyState icon="🔒" title="Access restricted" subtitle="Pipeline Trust configuration is only available to org admins and owners." />
      )}

      {activeTab === 'access-rules' && (
        rank >= 3
          ? <AccessRulesTab orgId={org.slug} canAdmin={canAdmin} />
          : <EmptyState icon="🔒" title="Access restricted" subtitle="Access rules are only available to org admins and owners." />
      )}

      {activeTab === 'policies' && (
        rank >= 2
          ? (
            <div style={{ display: 'grid', gap: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Link to={`/org/${org.slug}/policies`} className="btn btn-ghost btn-sm">Manage policies →</Link>
              </div>
              <OrgPolicies orgId={org.slug} canAdmin={canAdmin} />
            </div>
          )
          : <EmptyState icon="🔒" title="Access restricted" subtitle="Policies are only visible to org members and above." />
      )}

      {activeTab === 'billing' && (
        rank >= 2
          ? <OrgBilling orgId={org.slug} canAdmin={canAdmin} />
          : <EmptyState icon="🔒" title="Access restricted" subtitle="Billing information is only available to billing users." />
      )}

      {activeTab === 'notifications' && (
        rank >= 3
          ? <NotificationsSettings orgId={org.slug} canAdmin={canAdmin} />
          : <EmptyState icon="🔒" title="Access restricted" subtitle="Notification settings are only available to org admins and owners." />
      )}

      {activeTab === 'members' && (
      <>
      {rank < 2 && <EmptyState icon="🔒" title="Access restricted" subtitle="The member list is only available to org members and above." />}

      {/* Domain auto-join card - owners only */}
      {isOwner && (
        <div className={s.claimDomainCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text)' }}>Domain auto-join</span>
            {org.claimed_domain && (
              <span className="badge badge-blue">@{org.claimed_domain}</span>
            )}
          </div>
          {org.claimed_domain ? (
            <p style={{ fontSize: '0.82rem', color: 'var(--text2)', margin: 0 }}>
              New users who sign up with an <span style={{ fontFamily: 'var(--mono)' }}>@{org.claimed_domain}</span> address will automatically join this org as a reader.
            </p>
          ) : (
            <>
              <p style={{ fontSize: '0.82rem', color: 'var(--text2)', marginBottom: 14, marginTop: 0 }}>
                Claim your corporate email domain so that new signups with a matching address automatically join this org as a reader. Freemail providers (gmail.com, outlook.com, etc.) cannot be claimed.
              </p>
              <form onSubmit={(e) => { void handleClaimDomain(e) }} style={{ display: 'flex', gap: 8, maxWidth: 380 }}>
                <input
                  className="input"
                  placeholder="acme.com"
                  value={claimDomain}
                  onChange={(e) => setClaimDomain(e.target.value)}
                  required
                  style={{ flex: 1 }}
                />
                <button type="submit" className="btn btn-primary btn-sm" disabled={busy === 'claim'}>
                  {busy === 'claim' ? <span className="spinner" /> : 'Claim domain'}
                </button>
              </form>
            </>
          )}
        </div>
      )}

      {/* Members table */}
      {rank >= 2 && members.loading && (
        <SkeletonTable headers={['Email', 'Role', 'Billing User', 'Joined', 'Last active', ...(canAdmin ? [''] : [])]} firstColTwoLines={false} rows={4} />
      )}
      {rank >= 2 && !members.loading && members.error && (
        members.error.toLowerCase().includes('forbidden') || members.error.toLowerCase().includes('not authorized') || members.error.toLowerCase().includes('permission')
          ? <EmptyState icon="🔒" title="Access restricted" subtitle="You don't have permission to view members. Ask an org owner or admin to grant you access." />
          : <FeedbackMessage ok={false} msg={members.error} />
      )}
      {rank >= 2 && !members.loading && !members.error && (members.data?.members.length ?? 0) === 0 && (
        <div className="empty-state fu" style={{ marginBottom: 20 }}>
          <div>No members yet.</div>
        </div>
      )}
      {rank >= 2 && !members.loading && !members.error && (members.data?.members.length ?? 0) > 0 && (
        <div className="table-wrap fu" style={{ marginBottom: 20 }}>
          <table>
            <thead>
              <tr><th><Tooltip text="The member’s email address, as they sign in." placement="top"><span tabIndex={0} className="cursor-help">Email</span></Tooltip></th><th><Tooltip text="What the member can do in this org: reader, member, admin or owner." placement="top"><span tabIndex={0} className="cursor-help">Role</span></Tooltip></th><th><Tooltip text="Whether the member can see and manage billing." placement="top"><span tabIndex={0} className="cursor-help">Billing User</span></Tooltip></th><th><Tooltip text="When the member joined this org." placement="top"><span tabIndex={0} className="cursor-help">Joined</span></Tooltip></th><th><Tooltip text="When this person last used Terramantle - any signed-in request, not just a sign-in. Accurate to about five minutes, and platform-wide rather than per-org. Informational only: it changes nothing about what they can do." placement="top"><span tabIndex={0} className="cursor-help">Last active</span></Tooltip></th>{canAdmin && <th></th>}</tr>
            </thead>
            <tbody>
              {members.data!.members.map((m) => (
                <React.Fragment key={m.user_id}>
                  <tr>
                    <td style={{ fontFamily: 'var(--mono)', fontSize: '0.8rem' }}>{m.email}</td>
                    <td>
                      {canAdmin && m.role !== 'owner' ? (
                        <select
                          className="input"
                          style={{ padding: '3px 8px', fontSize: '0.75rem', width: 'auto' }}
                          value={m.role}
                          disabled={busy === m.user_id}
                          onChange={(e) => handleRoleChange(m.user_id, e.target.value)}
                        >
                          <option value="reader">reader</option>
                          <option value="member">member</option>
                          <option value="admin">admin</option>
                          {isOwner && <option value="owner">owner</option>}
                        </select>
                      ) : (
                        <RoleBadge role={m.role} />
                      )}
                    </td>
                    <td>
                      {isOwner ? (
                        <button
                          className={`btn btn-sm ${m.is_billing_admin ? 'btn-primary' : 'btn-ghost'}`}
                          style={{ fontSize: '0.72rem', padding: '2px 8px' }}
                          disabled={busy === m.user_id + '-billing'}
                          title={m.is_billing_admin ? 'Remove billing access' : 'Grant billing access'}
                          onClick={async () => {
                            setBusy(m.user_id + '-billing')
                            try {
                              await api.patch(`/api/orgs/${org.slug}/billing/admin/${m.user_id}`, { is_billing_admin: !m.is_billing_admin })
                              members.refetch()
                            } catch (err) {
                              showToast((err as Error).message, 'error')
                            } finally {
                              setBusy(null)
                            }
                          }}
                        >
                          {m.is_billing_admin ? 'billing' : '+ billing'}
                        </button>
                      ) : m.is_billing_admin ? (
                        <span className="badge badge-blue" style={{ fontSize: '0.7rem' }}>billing</span>
                      ) : null}
                    </td>
                    <td style={{ color: 'var(--text2)', fontSize: '0.8rem' }}>
                      {formatDate(m.created_at)}
                    </td>
                    <td style={{ color: 'var(--text2)', fontSize: '0.8rem' }}>
                      {/* Relative, because "is this seat in use" is a question about recency,
                          not about a date. Exact moment on hover. An older worker omits the
                          field entirely - that is "Unknown", not "Never". */}
                      {m.last_seen_at
                        ? <Tooltip text={formatDateTimeSeconds(m.last_seen_at)} placement="top">
                            <span tabIndex={0} className="cursor-help">{relativeTime(m.last_seen_at)}</span>
                          </Tooltip>
                        : <span className="dim">
                            {m.last_seen_at === undefined ? 'Unknown' : 'Never'}
                          </span>}
                    </td>
                    {canAdmin && (
                      <td>
                        {m.role !== 'owner' && (
                          <button
                            className="btn btn-danger btn-sm"
                            disabled={busy === m.user_id + '-del'}
                            onClick={() => setConfirmRemove(m.user_id)}
                          >
                            Remove
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                  {confirmRemove === m.user_id && (
                    <tr style={{ background: 'var(--bg3)' }}>
                      <td colSpan={6} style={{ padding: '10px 12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <span style={{ fontSize: '0.8rem', color: 'var(--text2)' }}>
                            Remove {m.email} from {org.name}?
                          </span>
                          <button
                            className="btn btn-danger btn-sm"
                            disabled={busy === m.user_id + '-del'}
                            onClick={() => handleRemove(m.user_id)}
                          >
                            {busy === m.user_id + '-del' ? <span className="spinner" /> : 'Confirm'}
                          </button>
                          <button className="btn btn-ghost btn-sm" onClick={() => setConfirmRemove(null)}>
                            Cancel
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isOwner ? (
        <>
          <div className="divider" style={{ marginTop: 40 }} />
          <div style={{ marginTop: 40 }}>
            <h3 style={{ color: 'var(--red)', marginBottom: 12 }}>Danger zone</h3>
            <p style={{ fontSize: '0.85rem', color: 'var(--text2)', marginBottom: 16 }}>
              Stop all artefact consumption for this organisation, or delete the organisation and
              all associated data.
            </p>
            <ConsumptionBlockPanel orgSlug={org.slug} />
            <DeleteOrgSection org={org} />
          </div>
        </>
      ) : (
        <>
          <div className="divider" style={{ marginTop: 40 }} />
          <div style={{ marginTop: 40 }}>
            <h3 style={{ color: 'var(--red)', marginBottom: 12 }}>Leave organisation</h3>
            <p style={{ fontSize: '0.85rem', color: 'var(--text2)', marginBottom: 16 }}>
              Remove yourself from <strong>{org.name}</strong>. You'll lose access to its
              modules, state, and policies. An owner or admin will need to re-invite you to rejoin.
            </p>
            <LeaveOrgSection org={org} />
          </div>
        </>
      )}
      </>
      )}
    </div>
  )
}

